"use client"

import { useEffect, useState } from "react"
import i18next from "i18next"
import {
  initReactI18next,
  useTranslation as useTranslationOrg,
} from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import resourcesToBackend from "i18next-resources-to-backend"
import { getOptions, languagesLocale, fallbackLng } from "./config"

const runsOnServerSide = typeof window === "undefined"

//
i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(async (language: string, namespace: string) => {
      // Fetch from static asset instead of webpack-bundling. Keeps the
      // client bundle lean and lets the browser cache locale JSON normally.
      // Same loader pattern as src/i18n/server.ts.
      try {
        const response = await fetch(`/locales/${language}/${namespace}.json`)
        if (!response.ok) return {}
        return await response.json()
      } catch {
        return {}
      }
    })
  )
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ["path", "htmlTag", "cookie", "navigator"],
    },
    preload: runsOnServerSide ? languagesLocale : [],
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      useSuspense: false,
    },
  })

export function useTranslation(lng, ns, options = undefined) {
  if (!runsOnServerSide && lng && i18next.resolvedLanguage !== lng) {
    i18next.changeLanguage(lng)
  }

  const ret = useTranslationOrg(ns, options)
  const { i18n } = ret
  if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng)
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (activeLng === i18n.resolvedLanguage) return
      setActiveLng(i18n.resolvedLanguage)
    }, [activeLng, i18n.resolvedLanguage])
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!lng || i18n.resolvedLanguage === lng) return
      i18n.changeLanguage(lng)
    }, [lng, i18n])
  }
  return ret
}
